import React from 'react';
import { addToPriorityQueue } from '../../utils/api';
import { InitiativesDB } from '../../utils/initiativesData';
import StrategicInitiativesGate from '../strategic/StrategicInitiativesGate';
import StrategicInitiativesList from '../strategic/StrategicInitiativesList';
import { Button } from '../ui/button';

function ManagerInitiativesTab({ unitId }) {
  if (InitiativesDB.isFeatureEnabled('manager')) {
    return (
      <StrategicInitiativesGate currentRole="manager">
        <StrategicInitiativesList />
      </StrategicInitiativesGate>
    );
  }

  const handleAddToQueue = async (payload) => {
    try {
      await addToPriorityQueue({
        title: payload.title,
        description: payload.metric || '',
        category: 'Initiatives',
        priority: payload.priority || 'medium',
        estimatedTime: 60,
        dueDate: new Date().toISOString(),
        type: 'task',
        ...payload
      });
    } catch (err) {
      console.error('Add to queue failed:', err);
    }
  };

  return (
    <div className="dashboard-container">
      <div className="section-header">
        <div>
          <h2 className="section-title">Strategic Initiatives</h2>
          <p className="section-subtitle">Practice-level initiatives and goals</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative border-l-4 border-l-green-nn">
          <div className="p-5">
            <div className="text-[13px] text-text-dark-secondary font-normal mb-2 block">Active Initiatives</div>
            <div className="text-[34px] font-bold text-text-dark tracking-tight mb-1.5 block">4</div>
            <div className="flex items-center gap-1 text-[13px] mb-4 text-green-nn">1 this quarter</div>
            <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-nn-border">
              <Button
                variant="default"
                size="sm"
                type="button"
                onClick={() => handleAddToQueue({ title: 'Review active initiatives', metric: 'Active Initiatives' })}
              >
                Add to Queue
              </Button>
            </div>
          </div>
        </div>

        <div className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative border-l-4 border-l-yellow-nn">
          <div className="p-5">
            <div className="text-[13px] text-text-dark-secondary font-normal mb-2 block">On Track</div>
            <div className="text-[34px] font-bold text-text-dark tracking-tight mb-1.5 block">3</div>
            <div className="flex items-center gap-1 text-[13px] mb-4 text-green-nn">75% on track</div>
            <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-nn-border">
              <Button
                variant="default"
                size="sm"
                type="button"
                onClick={() => handleAddToQueue({ title: 'Update initiative status', metric: 'Initiative Tracking' })}
              >
                Add to Queue
              </Button>
            </div>
          </div>
        </div>

        <div className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative border-l-4 border-l-yellow-nn">
          <div className="p-5">
            <div className="text-[13px] text-text-dark-secondary font-normal mb-2 block">Needs Attention</div>
            <div className="text-[34px] font-bold text-text-dark tracking-tight mb-1.5 block">1</div>
            <div className="flex items-center gap-1 text-[13px] mb-4 text-red-critical">Behind schedule</div>
            <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-nn-border">
              <Button
                variant="default"
                size="sm"
                type="button"
                onClick={() => handleAddToQueue({ title: 'Address initiative behind schedule', metric: 'Delayed Initiative', priority: 'high' })}
              >
                Add to Queue
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="section-header" style={{ marginTop: '32px' }}>
        <h3 className="section-title">Current Initiatives</h3>
      </div>

      <div className="space-y-4">
        {[
          { name: 'Patient Satisfaction Improvement', status: 'on-track', due: 'Q4 2025' },
          { name: 'Staff Engagement Program', status: 'on-track', due: 'Dec 2025' },
          { name: 'Access Optimization', status: 'on-track', due: 'Jan 2026' },
          { name: 'Quality Metric Rollout', status: 'at-risk', due: 'Nov 2025' }
        ].map((init, idx) => (
          <div key={idx} className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative border-l-4 border-l-green-nn">
            <div className="p-5">
              <div className="flex justify-between items-start">
                <div>
                  <div className="font-semibold text-gray-900">{init.name}</div>
                  <div className="text-sm text-gray-600 mt-1">Due: {init.due}</div>
                </div>
                <span className={`px-2 py-1 text-xs font-medium rounded ${
                  init.status === 'on-track' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {init.status === 'on-track' ? 'On Track' : 'Needs Attention'}
                </span>
              </div>
              <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-nn-border">
                <Button
                  variant="default"
                  size="sm"
                  type="button"
                  onClick={() => handleAddToQueue({ title: `Review ${init.name}`, metric: init.name, priority: init.status === 'at-risk' ? 'high' : 'medium' })}
                >
                  Add to Queue
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default ManagerInitiativesTab;
